import {BasicDataTable} from "@/shared/ui/basicDataTable";
import * as GPSPositionUtil from "@/shared/recording/gpsPositionUtil";
import {RecordingMapViewer} from "./recordingMapViewer";
import {formatDateTime, parseISOZonedDateTime} from "@/shared/utils/datefunctions";
import {GPSPosition, ReportRecord, RunningEntry} from "@/shared/timr";
import {DeletedStatus, Platform} from "@/shared/timrTypes";
import {isDriveLog} from "@/shared/utils/misc";

// L (Leaflet) is loaded globally
declare const L: typeof import("leaflet");

export interface RecordingGpsPositionPopoverConfig {
	baseElement: JQuery;
	selector: string;
	dataTable: DataTables.Api;
	isRunningRecording: boolean;
}

export class RecordingGpsPositionPopover {

	private static PADDING_MAP_TOP_LEFT: [number, number] = [10, 15];
	private static PADDING_MAP_BOTTOM_RIGHT: [number, number] = [10, 55];
	private static SHOW_START_END_POSITION_CLUSTER_DISTANCE = 250; // distance in meters

	private config: RecordingGpsPositionPopoverConfig;

	constructor(config: RecordingGpsPositionPopoverConfig) {
		this.config = config;
		this.init();
	}

	private init() {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const that = this;
		this.config.baseElement.popover(<PopoverOptions>{
			trigger: "hover",
			selector: this.config.selector,
			placement: "top",
			container: ".content-wrapper",
			delay: {show: 500, hide: 0},
			html: true,
			template: `<div class="popover position-popover" role="tooltip"><div class="arrow"></div><div class="popover-content"></div></div>`,
			content: function (this: HTMLElement) {
				const td = $(this).closest("td.gps-position");
				const recording: ReportRecord | RunningEntry = BasicDataTable.getRowData(that.config.dataTable, td);
				return that.isRunningEntry(recording) ?
					that.buildRunningRecordingPositionPopoverContent(recording) : that.buildPositionPopoverContent(recording);
			}
		});
		this.config.baseElement.on("shown.bs.popover", this.config.selector, function (this: HTMLElement) {
			const td = $(this).closest("td.gps-position");
			const recording: ReportRecord | RunningEntry = BasicDataTable.getRowData(that.config.dataTable, td);
			// Cast the Bootstrap popover instance since the library doesn't ship typed data for $.data("bs.popover").
			const popover = $(this).data("bs.popover") as { tip: () => JQuery };
			const tip = $(popover?.tip());

			if (tip.length === 0 || (that.isReportRecord(recording) && GPSPositionUtil.onlyDeletedPositionsAvailable(recording))) {
				return;
			}
			that.renderPositionPopoverMap(recording, tip);
		});
	}

	private isRunningEntry(_recording: ReportRecord | RunningEntry): _recording is RunningEntry {
		return this.config.isRunningRecording;
	}

	private isReportRecord(_recording: ReportRecord | RunningEntry): _recording is ReportRecord {
		return !this.config.isRunningRecording;
	}

	/**
	 * Builds the content of the GPS position popover
	 * Note that recordingTableGpsPopover.jsp has to be included in the page that contains the dataTable
	 * This jsp contains the .gps-position-popover DOM
	 * @param recording
	 * @private
	 */
	private buildPositionPopoverContent(recording: ReportRecord): string {
		const positionPopover = $(".gps-position-popover");

		const startPositionDeleted = recording.startPosition.deletedStatus && recording.startPosition.deletedStatus !== DeletedStatus.NOT_DELETED;
		const endPositionDeleted = recording.endPosition.deletedStatus && recording.endPosition.deletedStatus !== DeletedStatus.NOT_DELETED;
		const startPositionAvailable = recording.startPosition?.id && !startPositionDeleted;
		const endPositionAvailable = recording.endPosition?.id && !endPositionDeleted;
		if (startPositionAvailable) {
			this.setPositionPopoverAddressFields(recording.startPosition, positionPopover.find(".start-address-column"));
		}
		if (endPositionAvailable) {
			this.setPositionPopoverAddressFields(recording.endPosition, positionPopover.find(".end-address-column"));
		}

		this.togglePositionPopoverOnePositionInfo(positionPopover.find(".gps-position-panel-body-start"), recording.startPlatform, startPositionAvailable, startPositionDeleted);
		this.togglePositionPopoverOnePositionInfo(positionPopover.find(".gps-position-panel-body-end"), recording.endPlatform, endPositionAvailable, endPositionDeleted);

		if (startPositionAvailable || endPositionAvailable) {
			return positionPopover.html();
		} else {
			return $(".gps-position-popover-deleted").html();
		}
	}

	/**
	 * Build the GPS position popover content of a running recording
	 * Note that recordingTableGpsPopover.jsp has to be included in the page that contains the dataTable
	 * This jsp contains the .gps-position-popover DOM
	 * @param recording
	 * @private
	 */
	private buildRunningRecordingPositionPopoverContent(recording: RunningEntry): string {
		const positionPopover = $(".gps-position-popover");

		const startPositionAvailable = recording.startPosition?.id && recording.startPosition?.deletedStatus === DeletedStatus.NOT_DELETED;
		if (startPositionAvailable) {
			this.setPositionPopoverAddressFields(recording.startPosition, positionPopover.find(".start-address-column"));
			this.showRunningRecordingInfo(positionPopover.find(".gps-position-panel-body-end"));
		}
		if (startPositionAvailable) {
			return positionPopover.html();
		} else {
			return $(".gps-position-popover-deleted").html();
		}
	}

	/**
	 * The dataTable shows a running record. Show a message that the end position is missing.
	 * @param positionPopover DOM of the position popover
	 * @private
	 */
	private showRunningRecordingInfo(positionPopover: JQuery) {
		positionPopover.find(".gps-position-column").hide();
		positionPopover.find(".running-record-info").show();
	}

	/**
	 * Show information instead of address / latitude and longitude, if the record only has a start or an end position
	 * @param positionPopover DOM of the popover
	 * @param platform Platform info of the saved recording to build the message
	 * @param positionAvailable
	 * @param deleted Whether the GPS position got deleted
	 * @private
	 */
	private togglePositionPopoverOnePositionInfo(positionPopover: JQuery, platform: Platform, positionAvailable: boolean, deleted: boolean) {
		if (deleted) {
			positionPopover.find(".one-position-info > span").text(messages[`record.details.onePosition.deleted`]);
			positionPopover.find(".gps-position-column").css("display", "none");
			positionPopover.find(".one-position-info").css("display", "flex");
		} else if (!positionAvailable) {
			positionPopover.find(".one-position-info > span").text(messages[`record.details.onePosition.${platform}`]);
			positionPopover.find(".gps-position-column").css("display", "none");
			positionPopover.find(".one-position-info").css("display", "flex");
		} else {
			positionPopover.find(".gps-position-column").css("display", "flex");
			positionPopover.find(".one-position-info").css("display", "none");
		}
	}

	protected renderPositionPopoverMap(recording: ReportRecord | RunningEntry, tooltip: JQuery) {
		const map = L.map(tooltip.find(".position-popover-map")[0], {
			zoom: 12,
			attributionControl: false,
			layers: [L.tileLayer("https://{s}.tile.osm.org/{z}/{x}/{y}.png", {
				attribution: "&copy; <a href=\"http://osm.org/copyright\">OpenStreetMap</a> contributors",
				zIndex: 1
			})],
			zoomControl: false,
			dragging: false,
			touchZoom: false,
			doubleClickZoom: false,
			scrollWheelZoom: false
		});
		L.control.attribution({
			position: "topright"
		}).addTo(map);

		const recordingMapViewer = new RecordingMapViewer({
			map: map,
			fitBoundsOptions: {
				paddingTopLeft: RecordingGpsPositionPopover.PADDING_MAP_TOP_LEFT,
				paddingBottomRight: RecordingGpsPositionPopover.PADDING_MAP_BOTTOM_RIGHT,
				maxZoom: 12
			}
		});
		if (this.isReportRecord(recording) && isDriveLog(recording) && recording.hasTrack) {
			recordingMapViewer.loadTrack(recording);
		} else {
			this.displayPositionPopoverMarkers(recording, map, recordingMapViewer);
		}
	}

	/**
	 * Display the GPS position markers if available and fit bounds of the map afterwards
	 * fitBoundsOptions ensures, that the markers are visible and not cut off
	 * @param recording
	 * @param map Leaflet map of the position popover
	 * @param recordingMapViewer RecordingMapViewer used to render map markers
	 * @private
	 */
	private displayPositionPopoverMarkers(recording: ReportRecord | RunningEntry, map: L.Map, recordingMapViewer: RecordingMapViewer) {
		const startPositionAvailable = recording.startPosition?.id && recording.startPosition?.deletedStatus === DeletedStatus.NOT_DELETED;
		const endPositionAvailable = this.isReportRecord(recording) ? recording.endPosition?.id && recording.endPosition?.deletedStatus === DeletedStatus.NOT_DELETED : null;

		const startLatLng = startPositionAvailable ? L.latLng(recording.startPosition.latitude, recording.startPosition.longitude) : null;
		const endLatLng = endPositionAvailable && this.isReportRecord(recording) ? L.latLng(recording.endPosition.latitude, recording.endPosition.longitude) : null;

		if (this.isReportRecord(recording) && this.isEnoughSpaceBetweenStartEndPosition(recording, map, startPositionAvailable, endPositionAvailable)) {
			recordingMapViewer.displayIdenticalMarker(startLatLng);
		} else {
			if (startPositionAvailable) {
				recordingMapViewer.displayStartMarker(startLatLng);
			}
			if (endPositionAvailable) {
				recordingMapViewer.displayEndMarker(endLatLng);
			}
		}
		if (startPositionAvailable && endPositionAvailable) {
			map.fitBounds(L.latLngBounds(startLatLng, endLatLng), {
				paddingTopLeft: RecordingGpsPositionPopover.PADDING_MAP_TOP_LEFT,
				paddingBottomRight: RecordingGpsPositionPopover.PADDING_MAP_BOTTOM_RIGHT,
				maxZoom: 12
			});
		} else {
			map.panTo(startPositionAvailable ? startLatLng : endLatLng);
		}
	}

	/**
	 * Fill up the fields with address data, if available, otherwise the latitude and longitude
	 * @param gpsPosition GPS position of the recording, start or end position
	 * @param field DOM of the e start or end position field
	 * @private
	 */
	private setPositionPopoverAddressFields(gpsPosition: GPSPosition, field: JQuery) {
		const accuracyValue = $.isNumeric(gpsPosition.horizontalAccuracy) ?
			Number(gpsPosition.horizontalAccuracy).toFixed(0) + " m" : messages["position.accuracy.unknown"];

		if (gpsPosition.formattedAddress) {
			field.find(".formatted-address")
				.show()
				.html(gpsPosition.formattedAddress.replace(/,/g, "<br>"));
			field.find(".longitude-latitude").empty();
			field.find(".accuracy")
				.html(gpsPosition.latitude + ", " + gpsPosition.longitude + " (" + accuracyValue + ")")
				.prop("title", messages["record.details.latitude.longitude.tooltip"] + ", " + messages["position.accuracy"]);
		} else {
			field.find(".formatted-address").hide();
			field.find(".longitude-latitude")
				.text(gpsPosition.latitude + ", " + gpsPosition.longitude)
				.prop("title", messages["record.details.latitude.longitude.tooltip"]);
			field.find(".accuracy")
				.text(messages["position.accuracy"] + ": " + accuracyValue)
				.prop("title", "");
		}
		const timestamp = parseISOZonedDateTime(gpsPosition.timestamp);
		field.find(".timestamp")
			.empty()
			.text(formatDateTime(timestamp));
		if (globals.showTimeZone) {
			field.find(".timestamp")
				.append(`<span class="text-timezone"> ${timestamp?.offset ?? ""}</span>`);
		}
	}

	/**
	 * If the start- and end-position are too close to each other, the icons can no longer be recognized separately
	 * check the distance if it's below a defined value (SHOW_START_END_POSITION_CLUSTER_DISTANCE)
	 * @param recording
	 * @param map Leaflet map of the position popover
	 * @param startPositionAvailable
	 * @param endPositionAvailable
	 * @protected
	 */
	private isEnoughSpaceBetweenStartEndPosition(recording: ReportRecord, map: L.Map, startPositionAvailable: boolean, endPositionAvailable: boolean): boolean {
		return startPositionAvailable && endPositionAvailable
			&& map.distance(
				L.latLng(recording.startPosition.latitude, recording.startPosition.longitude),
				L.latLng(recording.endPosition.latitude, recording.endPosition.longitude)
			) < RecordingGpsPositionPopover.SHOW_START_END_POSITION_CLUSTER_DISTANCE;
	}
}
